import { formatDollars } from '@fruitful/fruitils';
import {
    type MembershipProfileAnswerWithQuestion,
    type MembershipProfileBillsAnswer,
} from '@fruitful/member-profile';
import {
    BillActivationStatus,
    BillCategory,
    BillCategoryNames,
    BillCreationType,
    BillingCadence,
} from '@fruitful/types';
import { atom, useAtom } from 'jotai';
import { capitalize } from 'lodash';
import { Badge } from 'primereact/badge';
import { Column } from 'primereact/column';
import {
    DataTable,
    type DataTableExpandedRows,
    type DataTableValueArray,
} from 'primereact/datatable';
import { InputSwitch } from 'primereact/inputswitch';
import { TabPanel, TabView } from 'primereact/tabview';
import React, { useEffect, useState } from 'react';
import {
    BillCategoryIcons,
    BodyLargeEmphasized,
    BodyLargeRegular,
    BodyMediumEmphasized,
    BodyMediumRegular,
    BodySmallEmphasized,
    BodySmallRegular,
    type ColorTokenKey,
    EditIcon,
    FootnoteRegular,
    Icon,
    IconButton,
    PlusIcon,
    Spinner,
    tokens,
    View,
    XStack,
    YStack,
} from 'smoothie';

import {
    MonospaceDetailText,
    MonospaceNumericText,
} from '../../../common/components/GridCellRenderers';
import { TextError } from '../../../common/components/InlineError';
import {
    centsToFormattedDollars,
    formatToSimpleDateString,
} from '../../../common/utils/formatting';
import {
    type Bill,
    type MemberDetails,
    type MembershipDetails,
    MembershipType,
} from '../../entityTypes';
import { useMembershipBills } from '../hooks/useMembershipBills';
import { BillCreateModal } from './BillCreateModal';
import { BillEditModal } from './BillEditModal';

const BillActivationStatusBackgroundColors: Record<
    BillActivationStatus,
    string
> = {
    [BillActivationStatus.ACTIVE]: tokens.color.light.wasabi.wasabi300,
    [BillActivationStatus.REVIEW]: tokens.color.light.tomatoRed.tomatoRed300,
    [BillActivationStatus.REJECTED]: tokens.color.light.gray.gray300,
    [BillActivationStatus.REMOVED]: tokens.color.light.gray.gray300,
    [BillActivationStatus.UNKNOWN]: tokens.color.light.marigold.marigold300,
};

const BillCategoryBackgroundColors: Record<BillCategory, string> = {
    [BillCategory.CHILDCARE]: tokens.color.light.peach.peach300,
    [BillCategory.INSURANCE]: tokens.color.light.tomatoRed.tomatoRed300,
    [BillCategory.LOANS]: tokens.color.light.dropletBlue.dropletBlue300,
    [BillCategory.OTHER]: tokens.color.light.gray.gray300,
    [BillCategory.SUBSCRIPTIONS]: tokens.color.light.marigold.marigold300,
    [BillCategory.RENT_MORTGAGE]: tokens.color.light.wasabi.wasabi300,
    [BillCategory.UTILITIES]: tokens.color.light.sweetViolet.sweetViolet300,
    [BillCategory.CREDIT_CARD_MINIMUM]: tokens.color.light.gray.gray400,
};

const BillCategoryTextColors: Record<BillCategory, string> = {
    [BillCategory.CHILDCARE]: tokens.color.light.peach.peach1200,
    [BillCategory.INSURANCE]: tokens.color.light.tomatoRed.tomatoRed1200,
    [BillCategory.LOANS]: tokens.color.light.dropletBlue.dropletBlue1200,
    [BillCategory.OTHER]: tokens.color.light.gray.gray1200,
    [BillCategory.SUBSCRIPTIONS]: tokens.color.light.marigold.marigold1200,
    [BillCategory.RENT_MORTGAGE]: tokens.color.light.wasabi.wasabi1200,
    [BillCategory.UTILITIES]: tokens.color.light.sweetViolet.sweetViolet1200,
    [BillCategory.CREDIT_CARD_MINIMUM]: tokens.color.light.gray.gray1200,
};

const BillActivationStatusColors: Record<BillActivationStatus, string> = {
    [BillActivationStatus.ACTIVE]: tokens.color.light.peach.peach1200,
    [BillActivationStatus.REVIEW]: tokens.color.light.tomatoRed.tomatoRed1200,
    [BillActivationStatus.REJECTED]:
        tokens.color.light.dropletBlue.dropletBlue1200,
    [BillActivationStatus.REMOVED]: tokens.color.light.gray.gray1200,
    [BillActivationStatus.UNKNOWN]: tokens.color.light.marigold.marigold1200,
};

const AmountRenderer = ({ amount }: Bill) => (
    <MonospaceNumericText>
        {centsToFormattedDollars(amount)}
    </MonospaceNumericText>
);

const MonthlyAmountRenderer = ({ monthlyAmount }: Bill) => (
    <MonospaceNumericText>
        {centsToFormattedDollars(monthlyAmount)}
    </MonospaceNumericText>
);

const MonthlyAverageRenderer = ({ monthlyAverage }: BillCategorySummary) => (
    <MonospaceNumericText>
        {centsToFormattedDollars(monthlyAverage)}
    </MonospaceNumericText>
);

const NameRenderer = ({ displayName, name, payee }: Bill) => (
    <BodyLargeRegular>{displayName ?? name ?? payee}</BodyLargeRegular>
);

const CategoryRenderer = ({ category: categoryString }: Bill) => {
    const category =
        (BillCategory[categoryString?.toUpperCase() ?? ''] as BillCategory) ??
        BillCategory.OTHER;

    return (
        <Badge
            value={BillCategoryNames[category]}
            style={{
                color: BillCategoryTextColors[category],
                backgroundColor: BillCategoryBackgroundColors[category],
                minWidth: '6rem',
            }}
        />
    );
};

const ActivationStatusRenderer = ({ activationStatus }: Bill) => {
    const status =
        (BillActivationStatus[
            activationStatus?.toUpperCase() ?? ''
        ] as BillActivationStatus) ?? BillActivationStatus.UNKNOWN;

    return (
        <Badge
            value={BillActivationStatus[status]}
            style={{
                color: BillActivationStatusColors[status],
                backgroundColor: BillActivationStatusBackgroundColors[status],
                minWidth: '6rem',
            }}
        />
    );
};

const CategorySummaryRenderer = ({ category, name }: BillCategorySummary) => {
    return (
        <XStack gap={16} alignItems="center">
            <View
                backgroundColor={BillCategoryBackgroundColors[category]}
                padding={8}
                borderRadius={8}
            >
                <Icon
                    icon={BillCategoryIcons[category]}
                    color={BillCategoryTextColors[category] as ColorTokenKey}
                    size={18}
                />
            </View>
            <BodyLargeEmphasized>{name}</BodyLargeEmphasized>
        </XStack>
    );
};

const CadenceRenderer = ({
    bill,
    showMonthly,
}: {
    bill: Bill;
    showMonthly: boolean;
}) => {
    const { cadence, lastPaidAt } = bill;
    return (
        <YStack>
            {showMonthly && cadence !== BillingCadence.MONTHLY ? (
                <XStack gap={8}>
                    <FootnoteRegular>
                        {capitalize(BillingCadence.MONTHLY)}
                    </FootnoteRegular>
                    <FootnoteRegular
                        textDecorationLine="line-through"
                        color="$gray900"
                    >
                        {capitalize(cadence)}
                    </FootnoteRegular>
                </XStack>
            ) : (
                <FootnoteRegular>{capitalize(cadence)}</FootnoteRegular>
            )}
            <XStack gap={4} alignItems="center">
                <MonospaceDetailText>Paid:</MonospaceDetailText>
                <MonospaceDetailText>
                    {formatToSimpleDateString(lastPaidAt) || 'Unknown'}
                </MonospaceDetailText>
            </XStack>
        </YStack>
    );
};

const CreationRenderer = ({
    bill,
    members,
    membershipType,
}: {
    bill: Bill;
    members: MemberDetails[];
    membershipType: MembershipType;
}) => {
    const { createdAt, creationType, userId } = bill;
    const shouldShowMember =
        membershipType !== MembershipType.SOLO && userId !== undefined;
    const member = shouldShowMember
        ? members.find((m) => m.id === userId)
        : undefined;

    return (
        <YStack>
            <BodySmallRegular>
                {formatToSimpleDateString(createdAt)}
            </BodySmallRegular>
            <XStack gap={4} alignItems="center">
                <MonospaceDetailText>
                    {creationType === BillCreationType.AUTO
                        ? 'AUTOMATIC'
                        : creationType}
                </MonospaceDetailText>
                {shouldShowMember && (
                    <FootnoteRegular>
                        {member?.firstName ?? userId}
                    </FootnoteRegular>
                )}
            </XStack>
        </YStack>
    );
};

export const BillsTabPanel = ({
    membershipDetails,
    billsProfileAnswer,
}: {
    membershipDetails: MembershipDetails;
    billsProfileAnswer?: MembershipProfileAnswerWithQuestion<MembershipProfileBillsAnswer>;
}) => {
    const [showMonthlyAmount, setShowMonthlyAmount] = useState(false);
    const { isLoadingBills, isBillsError } = useMembershipBills(
        membershipDetails.id,
    );

    const [editingBill, setEditingBill] = useState<Bill | null>(null);
    const [createBillModalOpen, setCreateBillModalOpen] =
        useState<boolean>(false);

    if (isBillsError) {
        return <TextError message="Error loading bills data" />;
    }

    if (isLoadingBills) {
        return (
            <XStack alignItems="center" gap={8}>
                <View>
                    <Spinner />
                </View>
                <BodyMediumRegular fontStyle="italic">
                    Loading bills data...
                </BodyMediumRegular>
            </XStack>
        );
    }

    const { bills = [] } = membershipDetails;
    const confirmedBills = bills.filter(
        (bill) => bill.activationStatus === BillActivationStatus.ACTIVE,
    );
    const pendingBills = bills.filter(
        (bill) => bill.activationStatus === BillActivationStatus.REVIEW,
    );
    const totalConfirmedBillsAmount = confirmedBills.reduce(
        (total, bill) => total + bill.monthlyAmount,
        0,
    );
    const totalPendingBillsAmount = pendingBills.reduce(
        (total, bill) => total + bill.monthlyAmount,
        0,
    );

    const spendAccounts =
        membershipDetails.holisticSummary?.spend.accounts ?? [];
    const saveAccounts = membershipDetails.holisticSummary?.save.accounts ?? [];
    const allBillAccounts = [...spendAccounts, ...saveAccounts];

    return (
        <YStack
            borderRadius="$4"
            backgroundColor="$white"
            padding="$5"
            gap="$4"
            shadowColor="$green800"
            shadowOffset={{ width: 0, height: 1 }}
            shadowOpacity={0.5}
            shadowRadius={4}
        >
            <XStack justifyContent="space-between" alignItems="center">
                <YStack
                    borderBottomWidth={1}
                    borderColor="$gray200"
                    paddingBottom="$2"
                    marginBottom="$4"
                >
                    <BodyLargeEmphasized>Bills</BodyLargeEmphasized>
                </YStack>
                <YStack
                    borderBottomWidth={1}
                    borderColor="$gray200"
                    paddingBottom="$2"
                    marginBottom="$4"
                >
                    <IconButton
                        variant="tertiary"
                        icon={PlusIcon}
                        onPress={() => {
                            setCreateBillModalOpen(true);
                        }}
                    />
                </YStack>
            </XStack>
            {billsProfileAnswer?.answer.answer != null && (
                <XStack gap={8} alignItems="center" marginBottom="$4">
                    <BodyMediumEmphasized>
                        Average monthly bills (3 Magic Numbers):
                    </BodyMediumEmphasized>
                    <BodyLargeEmphasized>
                        {formatDollars(billsProfileAnswer.answer.answer)}
                    </BodyLargeEmphasized>
                </XStack>
            )}
            <YStack gap={24}>
                <XStack width="100%" justifyContent="space-between">
                    <XStack gap={8}>
                        <BodyMediumEmphasized>
                            Average monthly bills amount (confirmed):
                        </BodyMediumEmphasized>
                        <BodyLargeEmphasized>
                            {isNaN(totalConfirmedBillsAmount)
                                ? 'Unknown'
                                : centsToFormattedDollars(
                                      totalConfirmedBillsAmount,
                                      false,
                                  )}
                        </BodyLargeEmphasized>
                    </XStack>
                    <BodySmallEmphasized>
                        {confirmedBills.length} bills confirmed for this
                        membership
                    </BodySmallEmphasized>
                </XStack>
                <XStack width="100%" justifyContent="space-between">
                    <XStack gap={8}>
                        <BodyMediumRegular>
                            Average monthly bills amount (pending review):
                        </BodyMediumRegular>
                        <BodyLargeRegular>
                            {isNaN(totalPendingBillsAmount)
                                ? 'Unknown'
                                : centsToFormattedDollars(
                                      totalPendingBillsAmount,
                                      false,
                                  )}
                        </BodyLargeRegular>
                    </XStack>
                    <BodySmallEmphasized>
                        {pendingBills.length} bills pending review for this
                        membership
                    </BodySmallEmphasized>
                </XStack>
                <XStack width="100%" justifyContent="space-between">
                    <XStack gap={8}>
                        <BodyMediumRegular>
                            Total average monthly (confirmed + pending)
                            bills:{' '}
                        </BodyMediumRegular>
                        <BodyLargeRegular>
                            {(isNaN(totalPendingBillsAmount) ??
                            isNaN(totalConfirmedBillsAmount))
                                ? 'Unknown'
                                : centsToFormattedDollars(
                                      totalPendingBillsAmount +
                                          totalConfirmedBillsAmount,
                                      false,
                                  )}
                        </BodyLargeRegular>
                    </XStack>
                    <BodySmallEmphasized>
                        {pendingBills.length + confirmedBills.length} bills
                        total for this membership
                    </BodySmallEmphasized>
                </XStack>
                {bills.length > 0 && (
                    <YStack>
                        <XStack
                            justifyContent="flex-end"
                            alignItems="center"
                            gap={8}
                            position="absolute"
                            top={16}
                            right={0}
                        >
                            <BodySmallRegular>
                                Show monthly amounts
                            </BodySmallRegular>
                            <InputSwitch
                                checked={showMonthlyAmount}
                                onChange={(e) => {
                                    setShowMonthlyAmount(e.value);
                                }}
                            />
                        </XStack>
                        <TabView
                            style={{ width: '100%' }}
                            panelContainerStyle={{
                                width: '100%',
                                padding: 0,
                                marginTop: 32,
                            }}
                        >
                            <TabPanel header="Bills by Category">
                                <BillCategoriesTable
                                    membershipDetails={membershipDetails}
                                    bills={bills}
                                    showMonthlyAmount={showMonthlyAmount}
                                    setEditingBill={setEditingBill}
                                />
                            </TabPanel>
                            <TabPanel header="All Bills">
                                <BillsTable
                                    membershipDetails={membershipDetails}
                                    bills={bills}
                                    showMonthlyAmount={showMonthlyAmount}
                                    setEditingBill={setEditingBill}
                                />
                            </TabPanel>
                        </TabView>
                    </YStack>
                )}
            </YStack>
            {editingBill && (
                <BillEditModal
                    bill={editingBill}
                    isOpen={true}
                    onClose={() => {
                        setEditingBill(null);
                    }}
                />
            )}
            {createBillModalOpen && (
                <BillCreateModal
                    isOpen={true}
                    onClose={() => {
                        setCreateBillModalOpen(false);
                    }}
                    accounts={allBillAccounts}
                />
            )}
        </YStack>
    );
};

interface BillCategorySummary {
    category: BillCategory;
    name: string;
    monthlyAverage: number;
    bills: Bill[];
}

const getBillCategorySummaries = (bills: Bill[]) => {
    const categorySummaries: Record<BillCategory, BillCategorySummary> = {
        [BillCategory.CHILDCARE]: {
            category: BillCategory.CHILDCARE,
            name: BillCategoryNames[BillCategory.CHILDCARE],
            monthlyAverage: 0,
            bills: [],
        },
        [BillCategory.CREDIT_CARD_MINIMUM]: {
            category: BillCategory.CREDIT_CARD_MINIMUM,
            name: BillCategoryNames[BillCategory.CREDIT_CARD_MINIMUM],
            monthlyAverage: 0,
            bills: [],
        },
        [BillCategory.INSURANCE]: {
            category: BillCategory.INSURANCE,
            name: BillCategoryNames[BillCategory.INSURANCE],
            monthlyAverage: 0,
            bills: [],
        },
        [BillCategory.LOANS]: {
            category: BillCategory.LOANS,
            name: BillCategoryNames[BillCategory.LOANS],
            monthlyAverage: 0,
            bills: [],
        },
        [BillCategory.OTHER]: {
            category: BillCategory.OTHER,
            name: BillCategoryNames[BillCategory.OTHER],
            monthlyAverage: 0,
            bills: [],
        },
        [BillCategory.SUBSCRIPTIONS]: {
            category: BillCategory.SUBSCRIPTIONS,
            name: BillCategoryNames[BillCategory.SUBSCRIPTIONS],
            monthlyAverage: 0,
            bills: [],
        },
        [BillCategory.RENT_MORTGAGE]: {
            category: BillCategory.RENT_MORTGAGE,
            name: BillCategoryNames[BillCategory.RENT_MORTGAGE],
            monthlyAverage: 0,
            bills: [],
        },
        [BillCategory.UTILITIES]: {
            category: BillCategory.UTILITIES,
            name: BillCategoryNames[BillCategory.UTILITIES],
            monthlyAverage: 0,
            bills: [],
        },
    };
    bills.forEach((bill) => {
        const category =
            (BillCategory[
                bill.category?.toUpperCase() ?? ''
            ] as BillCategory) ?? BillCategory.OTHER;
        categorySummaries[category].monthlyAverage += bill.monthlyAmount;
        categorySummaries[category].bills.push(bill);
    });
    // map the dictionary to an array
    return Object.entries(categorySummaries).map(([_, value]) => value);
};

const expandedBillCategories = atom<
    DataTableExpandedRows | DataTableValueArray | undefined
>(undefined);

const BillCategoriesTable = ({
    membershipDetails,
    bills,
    showMonthlyAmount,
    setEditingBill,
}: {
    membershipDetails: MembershipDetails;
    bills: Bill[];
    showMonthlyAmount: boolean;
    setEditingBill: (b: Bill) => void;
}) => {
    const [categoryRows, setCategoryRows] = useState<BillCategorySummary[]>(
        getBillCategorySummaries(bills),
    );
    useEffect(() => {
        setCategoryRows(getBillCategorySummaries(bills));
    }, [bills]);

    const [expandedRows, setExpandedRows] = useAtom(expandedBillCategories);
    const rowExpansionTemplate = (categorySummary: BillCategorySummary) => {
        return (
            <View paddingLeft="4rem" paddingBottom="2rem">
                <BillsTable
                    membershipDetails={membershipDetails}
                    bills={categorySummary.bills}
                    showMonthlyAmount={showMonthlyAmount}
                    setEditingBill={setEditingBill}
                />
            </View>
        );
    };

    return (
        <YStack>
            <DataTable
                value={categoryRows}
                size="small"
                tableStyle={{ width: '100%' }}
                sortField="monthlyAverage"
                sortOrder={-1}
                scrollable
                scrollHeight="70vh"
                rowExpansionTemplate={rowExpansionTemplate}
                expandedRows={expandedRows}
                onRowToggle={(e) => {
                    setExpandedRows(e.data);
                }}
            >
                <Column expander style={{ width: '4rem' }} />
                <Column
                    field="category"
                    header="Category"
                    sortable
                    body={CategorySummaryRenderer}
                    style={{ width: '20rem' }}
                />
                <Column
                    field="monthlyAverage"
                    header="Monthly average"
                    sortable
                    align="right"
                    body={MonthlyAverageRenderer}
                    style={{ width: '20rem' }}
                />
                <Column style={{ width: '30%' }} />
            </DataTable>
        </YStack>
    );
};

const EditButtonRenderer = (bill: Bill, onEdit: (b: Bill) => void) => {
    return (
        <IconButton
            variant="tertiary"
            icon={EditIcon}
            onPress={() => {
                onEdit(bill);
            }}
        />
    );
};

export const BillsTable = ({
    membershipDetails,
    bills,
    showMonthlyAmount,
    setEditingBill,
    showRowIndex = false,
    onBillRowClick,
}: {
    membershipDetails: MembershipDetails;
    bills: Bill[];
    showMonthlyAmount: boolean;
    setEditingBill: (b: Bill) => void;
    /** 1-based row numbers (e.g. Bills- Dev Tools for quick counts). */
    showRowIndex?: boolean;
    onBillRowClick?: (bill: Bill) => void;
}) => {
    const _CreationRenderer = (bill: Bill) => (
        <CreationRenderer
            bill={bill}
            members={membershipDetails.users}
            membershipType={membershipDetails.membershipType}
        />
    );
    const _CadenceRenderer = (bill: Bill) => (
        <CadenceRenderer bill={bill} showMonthly={showMonthlyAmount} />
    );

    return !bills.length ? (
        <BodyMediumRegular fontStyle="italic">No bills</BodyMediumRegular>
    ) : (
        <DataTable
            value={bills}
            size="small"
            tableStyle={{ width: '100%' }}
            sortField={showMonthlyAmount ? 'monthlyAmount' : 'amount'}
            sortOrder={-1}
            scrollable
            scrollHeight="70vh"
            onRowClick={
                onBillRowClick != null
                    ? (e) => {
                          const target = e.originalEvent?.target as
                              | HTMLElement
                              | undefined;
                          if (
                              target?.closest(
                                  'button, [role="button"], a, input, select, textarea',
                              )
                          ) {
                              return;
                          }
                          onBillRowClick(e.data as Bill);
                      }
                    : undefined
            }
        >
            {showRowIndex && (
                <Column
                    header="#"
                    style={{ width: '2.5rem', verticalAlign: 'top' }}
                    body={(_, options) => (
                        <BodySmallRegular color="$gray1000">
                            {options.rowIndex + 1}
                        </BodySmallRegular>
                    )}
                />
            )}
            <Column
                field="category"
                header="Category"
                sortable
                body={CategoryRenderer}
            />
            <Column
                field="activationStatus"
                header="Status"
                sortable
                body={ActivationStatusRenderer}
            />
            <Column
                field="displayName"
                header="Name"
                sortable
                body={NameRenderer}
            />
            <Column
                field="amount"
                header="Amount"
                sortable
                align="right"
                body={AmountRenderer}
                hidden={showMonthlyAmount}
            />
            <Column
                field="monthlyAmount"
                header="Monthly amount"
                sortable
                align="right"
                body={MonthlyAmountRenderer}
                hidden={!showMonthlyAmount}
            />
            <Column
                field="cadence"
                header="Cadence ⓘ"
                headerTooltip={`How often the bill is due, and when last paid (only available with active transaction monitoring). ${showMonthlyAmount ? 'Original cadence crossed out when showing monthly amounts.' : ''}`}
                headerTooltipOptions={{ position: 'top' }}
                body={_CadenceRenderer}
            />
            <Column
                field="createdAt"
                header="Origin ⓘ"
                headerTooltip="Shows the date the bill was added to Fruitful, whether 'Auto' or 'Manual', and the member who added the bill if a joint membership."
                headerTooltipOptions={{ position: 'top' }}
                body={_CreationRenderer}
            />
            <Column
                body={(rowData) => (
                    <XStack>
                        {EditButtonRenderer(rowData as Bill, setEditingBill)}
                    </XStack>
                )}
                style={{ width: '40px' }}
            />
        </DataTable>
    );
};
